import { useEffect, useState, useTransition } from 'react';
import { Check, Clock } from 'lucide-react';
import { useTranslations } from 'use-intl';
import {
  Avatar, Badge, Body, Button, Eyebrow, Heading, I, Icon, Dialog, DialogFooter, DialogSection, Label, Pill, Row, SkeletonText, Stack, Textarea, } from '@leanin/ui';
import type { IntakeFormKind } from '@leanin/shared/intake';
import { MAX_ANSWER_CHARS } from '@leanin/shared/intake';
import { useApplicantIntakeForm } from '@/lib/queries/groups';
import { trackIntakeFormSubmitted } from '@/lib/analytics/events';
import {
  IntakeFormFields,
  type AnswerPayload,
  type AnswerState,
  type ApplicantIntakeForm,
  type ApplicantQuestion,
  type ConsentPayload,
  answerComplete,
  answerTooLong,
  buildAnswers,
  buildConsent,
  classifyIntakeSubmitError,
  isSubmittable,
  mergeAnswers,
  missingRequiredCount,
  useIntakePrefill,
  useTrackIntakeFormViewed,
} from '@/components/intake';
import styles from './RequestToJoinGroupModal.module.css';

const NOTE_MAX = 1000;

// The submit payload: either the legacy `message` (flag off / no form) or the
// intake fields (formVersionId + answers + consent). The caller forwards it to
// `useJoinGroup`.
export type JoinSubmitInput = {
  message?: string;
  formVersionId?: string;
  answers?: AnswerPayload[];
  consent?: ConsentPayload;
};

export type JoinSubmitResult =
  | { ok: true }
  | { ok: false; code?: string; message: string };

export interface RequestToJoinGroupModalProps {
  open: boolean;
  onClose: () => void;
  groupId: string;
  groupKind: 'circle' | 'network';
  groupName: string;
  leader: { name: string; photoUrl: string | null; jobTitle: string | null } | null;
  onSubmit: (input: JoinSubmitInput) => Promise<JoinSubmitResult>;
  /** Withdraw the just-sent request. When provided, the post-submit
   *  confirmation shows a "Withdraw" action; on success the modal closes. */
  onWithdraw?: () => Promise<JoinSubmitResult>;
  /** Preview mode (leader "Preview form"): render the applicant form from a
   *  provided draft form instead of fetching, with submit disabled. Nothing is
   *  ever submitted — the leader just sees exactly what applicants will. */
  isPreview?: boolean;
  previewForm?: ApplicantIntakeForm | null;
}

export function RequestToJoinGroupModal({
  open,
  onClose,
  groupId,
  groupKind,
  groupName,
  leader,
  onSubmit,
  onWithdraw,
  isPreview = false,
  previewForm = null,
}: RequestToJoinGroupModalProps) {
  const t = useTranslations('Group');
  const formKind: IntakeFormKind =
    groupKind === 'circle' ? 'member_to_circle' : 'member_to_network';
  // Preview renders a provided draft form and never fetches.
  const formQuery = useApplicantIntakeForm(groupId, formKind, {
    enabled: open && !isPreview,
  });
  const form: ApplicantIntakeForm | null = isPreview
    ? previewForm
    : (formQuery.data?.form ?? null);
  // Loading only while open, before the first response.
  const loadingForm =
    !isPreview && open && formQuery.data === undefined && !formQuery.isError;
  // A genuine {form:null} at 200 means "no form applies" → plain note. A fetch
  // ERROR (5xx/network) is distinct: we can't know whether a form is required,
  // so we show a retry state rather than degrading to a note the server may
  // reject.
  const formLoadError = !isPreview && open && formQuery.isError;

  const [note, setNote] = useState('');
  const [answers, setAnswers] = useState<AnswerState>({});
  const [agreed, setAgreed] = useState<ReadonlySet<string>>(new Set());
  const [touched, setTouched] = useState<ReadonlySet<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();
  // Post-submit flow: 'form' (editing) → 'sent' (confirmation card). Reviewing a
  // submitted request happens in the directory's Pending Requests section, so
  // the confirmation no longer carries an in-modal review view.
  const [phase, setPhase] = useState<'form' | 'sent'>('form');
  const [isWithdrawing, startWithdraw] = useTransition();

  useEffect(() => {
    if (!open) {
      setNote('');
      setAnswers({});
      setAgreed(new Set());
      setTouched(new Set());
      setError(null);
      setPhase('form');
    }
  }, [open]);

  // (Re)seed answers whenever the form version changes — first load or a
  // stale-version refetch — preserving answers whose questionId still exists.
  // `prefill` seeds recommended region/sector questions from the profile; it
  // is stable once the profile resolves, so a late resolve re-seeds only the
  // untouched, still-blank prefill fields (touched/non-empty answers are
  // preserved). `touched` is read via closure, not a dep, so a blur doesn't
  // re-seed — the re-seed rides the formVersionId/prefill transitions.
  const prefill = useIntakePrefill();
  const formVersionId = form?.formVersionId;
  useEffect(() => {
    if (form !== null) setAnswers((prev) => mergeAnswers(form, prev, prefill, touched));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on formVersionId + prefill; `form`/`touched` read via closure
  }, [formVersionId, prefill]);

  useTrackIntakeFormViewed(form, formKind, open && !isPreview);

  const missing = form !== null ? missingRequiredCount(form, answers, agreed) : 0;
  const submittable = form !== null ? isSubmittable(form, answers, agreed) : true;

  // Inline errors reveal once a field has been touched (blurred). With
  // submit-disabled-until-complete, the disabled button + the live remaining
  // count carry the "not ready" signal; per-field errors just pinpoint a
  // touched-but-incomplete field.
  const errorFor = (q: ApplicantQuestion): string | null => {
    if (!touched.has(q.id)) return null;
    if (answerTooLong(answers[q.id])) {
      return t('intakeForm.errors.answerTooLong', { max: MAX_ANSWER_CHARS });
    }
    if (q.required && !answerComplete(q, answers[q.id])) {
      return t('intakeForm.errors.answerRequired');
    }
    return null;
  };

  const submit = () => {
    if (isPreview) return; // Preview never submits.
    setError(null);
    startTransition(async () => {
      let payload: JoinSubmitInput;
      if (form !== null) {
        // Send is disabled until submittable; this guard is defensive.
        if (!submittable) return;
        payload = {
          formVersionId: form.formVersionId,
          answers: buildAnswers(form, answers),
          consent: buildConsent(agreed),
        };
      } else {
        const message = note.trim();
        payload = message.length > 0 ? { message } : {};
      }

      const result = await onSubmit(payload);
      if (result.ok) {
        if (form !== null) {
          trackIntakeFormSubmitted({
            form_kind: formKind,
            form_version: form.versionNumber,
            question_count: form.questions.length,
          });
        }
        // Keep the modal open and show the "Request sent" confirmation instead
        // of closing. The applicant can review their answers or withdraw.
        setPhase('sent');
        return;
      }

      const kind = classifyIntakeSubmitError(result.code);
      if (kind === 'stale_version') {
        // The leader edited the form after it loaded. Pull the new version
        // (answers are preserved by questionId) and ask the applicant to review.
        void formQuery.refetch();
        setError(t('intakeForm.submitErrors.stale_version'));
        return;
      }
      setError(
        kind === 'required'
          ? t('intakeForm.submitErrors.required')
          : kind === 'too_large'
            ? t('intakeForm.submitErrors.too_large')
            : kind === 'malformed'
              ? t('intakeForm.submitErrors.malformed')
              : result.message,
      );
    });
  };

  const handleWithdraw = () => {
    if (!onWithdraw) return;
    setError(null);
    startWithdraw(async () => {
      const result = await onWithdraw();
      if (result.ok) {
        onClose();
        return;
      }
      setError(result.message);
    });
  };

  const leaderFirstName = leader?.name.split(' ')[0] ?? t('join.leaderFallback');
  const sendDisabled =
    isPreview || loadingForm || formLoadError || (form !== null && !submittable);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => { if (!v) onClose(); }}
      size="lg"
      accessibleTitle={t('join.accessibleTitle', { groupName })}
      className={phase === 'sent' ? `${styles.modal} ${styles.modalSent}` : styles.modal}
    >
      {phase === 'sent' ? (
        <DialogSection bleed>
          <Stack gap="md" align="center" className={styles.sent}>
            <Row justify="flex-end" style={{ width: '100%' }}>
              <Button
                variant="ghost"
                size="sm"
                icon={I.x}
                aria-label={t('join.close')}
                onClick={onClose}
                disabled={isWithdrawing}
              />
            </Row>
            <div className={styles.sentBadge}>
              <Check size={30} strokeWidth={2} color="var(--palette-emerald)" aria-hidden />
            </div>
            <Heading level={3} className={styles.title}>
              {t('join.sent.title')}
            </Heading>
            <Pill
              tone="yellow"
              icon={<Clock size="100%" strokeWidth={1.75} aria-hidden />}
              style={{ alignSelf: 'center' }}
            >
              {t('join.sent.pending')}
            </Pill>
            {error !== null && (
              <span role="alert" aria-live="polite">
                <Body size="sm" color="destructive">{error}</Body>
              </span>
            )}
            {onWithdraw ? (
              <Row gap="md" justify="center" wrap style={{ width: '100%' }}>
                <Button
                  variant="softDanger"
                  size="md"
                  onClick={handleWithdraw}
                  loading={isWithdrawing}
                >
                  {t('join.sent.withdraw')}
                </Button>
              </Row>
            ) : null}
          </Stack>
        </DialogSection>
      ) : null}
      {phase !== 'sent' ? (
        <DialogSection bleed>
          <Row
            justify="space-between"
            align="flex-start"
            gap="lg"
            className={styles.header}
          >
            <Stack gap="sm" style={{ minWidth: 0 }}>
              {isPreview && (
                <Eyebrow tone="default">{t('join.previewEyebrow')}</Eyebrow>
              )}
              <Heading level={3} className={styles.title}>
                {t('join.heading', { groupName })}
              </Heading>
              <Body color="mute" className={styles.subtitle}>
                {t.rich('join.subtitle', {
                  b: (chunks) => <strong>{chunks}</strong>,
                  kind: groupKind,
                })}
              </Body>
            </Stack>
            <Button
              variant="ghost"
              size="sm"
              icon={I.x}
              aria-label={t('join.close')}
              onClick={onClose}
              disabled={isPending}
            />
          </Row>
        </DialogSection>
      ) : null}
      {phase !== 'sent' ? (
        <DialogSection scrollable bleed>
          <Stack gap="lg" className={styles.body}>
            {leader !== null && (
              <Row gap="sm" align="center">
                <Avatar
                  {...(leader.photoUrl ? { src: leader.photoUrl } : {})}
                  name={leader.name}
                  size={28}
                />
                <Row gap="sm" align="center" wrap style={{ flex: 1, minWidth: 0 }}>
                  <Body size="sm" color="mute">
                    {t('join.requestGoesTo', { name: leaderFirstName })}
                  </Body>
                  <Pill tone="burgundy" icon={I.spark}>
                    {t('join.leadsThis', { kind: groupKind })}
                  </Pill>
                </Row>
              </Row>
            )}

            {loadingForm ? (
              <SkeletonText lines={4} size="md" />
            ) : formLoadError ? (
              <Stack gap="md">
                <Body size="sm" color="destructive">
                  {t('intakeForm.loadError')}
                </Body>
                <Row>
                  <Button
                    variant="secondary"
                    size="md"
                    onClick={() => {
                      void formQuery.refetch();
                    }}
                  >
                    {t('intakeForm.retry')}
                  </Button>
                </Row>
              </Stack>
            ) : form !== null ? (
              <Stack gap="md">
                <IntakeFormFields
                  form={form}
                  answers={answers}
                  agreed={agreed}
                  onAnswerChange={(id, value) => {
                    setAnswers((p) => ({ ...p, [id]: value }));
                    setError(null);
                  }}
                  onAnswerBlur={(id) => setTouched((p) => new Set(p).add(id))}
                  onConsentToggle={(key, next) =>
                    setAgreed((p) => {
                      const s = new Set(p);
                      if (next) s.add(key);
                      else s.delete(key);
                      return s;
                    })
                  }
                  errorFor={errorFor}
                  consentError={null}
                />
              </Stack>
            ) : (
              <Stack gap="sm">
                <Row justify="space-between" align="baseline">
                  <Label size="lg">{t('join.noteLabel')}</Label>
                  <Body size="sm" color="mute">
                    {t('join.optional')}
                  </Body>
                </Row>
                <Textarea
                  rows={4}
                  value={note}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v.length <= NOTE_MAX) setNote(v);
                  }}
                  placeholder={t('join.notePlaceholder', { kind: groupKind })}
                />
                <Row justify="space-between" align="center" gap="sm">
                  <Row gap="sm" align="center" style={{ minWidth: 0 }}>
                    <Icon size={14} color="var(--muted-foreground)">
                      {I.lock}
                    </Icon>
                    <Body size="sm" color="mute">
                      {t('join.messageVisibleTo', { kind: groupKind })}
                    </Body>
                  </Row>
                  <Body size="sm" color="mute">
                    {t('join.charCount', { count: note.length, max: NOTE_MAX })}
                  </Body>
                </Row>
              </Stack>
            )}

            {error !== null && (
              <span role="alert" aria-live="polite">
                <Body size="sm" color="destructive">
                  {error}
                </Body>
              </span>
            )}
          </Stack>
        </DialogSection>
      ) : null}
      {phase !== 'sent' ? (
        <DialogFooter className={styles.footer}>
          <Row
            justify="space-between"
            align="center"
            wrap
            gap="md"
            style={{ flex: 1, minWidth: 0 }}
          >
            <div aria-live="polite" style={{ minWidth: 0 }}>
              {form !== null ? (
                <Row gap="sm" align="center">
                  {missing > 0 ? (
                    <Badge
                      tone="primary"
                      style={{
                        height: '1.75rem',
                        minWidth: '1.75rem',
                        font: 'var(--type-label)',
                      }}
                    >
                      {missing}
                    </Badge>
                  ) : null}
                  <Body
                    size="sm"
                    color={missing === 0 ? 'success' : 'primary'}
                    className={styles.requiredText}
                  >
                    {t('intakeForm.requiredCount', { count: missing })}
                  </Body>
                </Row>
              ) : null}
            </div>
            <Row gap="md" align="center">
              <Button variant="ghost" size="lg" onClick={onClose} disabled={isPending}>
                {t('join.cancel')}
              </Button>
              <Button
                variant="primary"
                size="lg"
                iconRight={I.send}
                onClick={submit}
                loading={isPending}
                disabled={sendDisabled}
                style={
                  sendDisabled
                    ? {
                        background: 'var(--light-poppy)',
                        color: 'color-mix(in oklab, var(--primary) 55%, var(--light-poppy))',
                      }
                    : undefined
                }
              >
                {t('join.sendRequest')}
              </Button>
            </Row>
          </Row>
        </DialogFooter>
      ) : null}
    </Dialog>
  );
}
